import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { ArrowLeft, Mail, CheckCircle } from "lucide-react";
import { authAPI } from "../../services/api";
import toast from "react-hot-toast";
import BackgroundWrapper from "../../components/common/BackgroundWrapper";
import "./Auth.css";

const ForgotPassword = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetInfo, setResetInfo] = useState(null);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    getValues,
  } = useForm();

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      const response = await authAPI.forgotPassword(data.email);

      if (response.data.success) {
        setIsSubmitted(true);
        setResetInfo(response.data);
        toast.success("Password reset instructions sent!");
      }
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to send reset email";
      toast.error(message);
      setError("root", { message });
    } finally {
      setLoading(false);
    }
  };

  const handleTryAgain = () => {
    setIsSubmitted(false);
    setResetInfo(null);
  };

  /* ----------------------------
     SUCCESS VIEW
  ----------------------------- */
  if (isSubmitted) {
    return (
      <BackgroundWrapper background="/auth-grid.png">
        <div className="auth-wrapper">
          <div className="auth-card">
            <button onClick={() => navigate("/login")} className="back-btn">
              <ArrowLeft size={20} />
            </button>

            <div className="auth-success-icon">
              <CheckCircle size={60} />
            </div>

            <h2 className="auth-title-small">Email Sent!</h2>

            <p className="auth-subtext">
              We sent password reset instructions to:
              <br />
              <strong>{getValues("email")}</strong>
              <br />
              The link will expire in 15 minutes.
            </p>

            {/* DEVELOPMENT RESET LINK */}
            {process.env.NODE_ENV === "development" && resetInfo?.resetUrl && (
              <div className="dev-reset-box">
                <strong>Development Mode - Direct Link:</strong>
                <br />
                <a
                  href={resetInfo.resetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {resetInfo.resetUrl}
                </a>
              </div>
            )}

            <div className="auth-success-actions">
              <button className="auth-submit" onClick={handleTryAgain}>
                Try Different Email
              </button>

              <Link to="/login" className="auth-link-button">
                Back to Sign In
              </Link>
            </div>
          </div>
        </div>
      </BackgroundWrapper>
    );
  }

  /* ----------------------------
     FORM VIEW
  ----------------------------- */
  return (
    <BackgroundWrapper background="/auth-grid.png">
      <div className="auth-wrapper">
        <div className="auth-card">
          <button onClick={() => navigate(-1)} className="back-btn">
            <ArrowLeft size={20} />
          </button>

          <div className="auth-success-icon">
            <Mail size={60} />
          </div>

          <h2 className="auth-title-small">Reset Your Password</h2>
          <p className="auth-subtext">
            Enter your email and we'll send you a reset link.
          </p>

          <form onSubmit={handleSubmit(onSubmit)}>
            {errors.root && (
              <div className="error-message">{errors.root.message}</div>
            )}

            <input
              type="email"
              placeholder="Email Address"
              className="auth-input"
              {...register("email", {
                required: "Email is required",
              })}
            />

            {errors.email && (
              <span className="form-error">{errors.email.message}</span>
            )}

            <button type="submit" className="auth-submit" disabled={loading}>
              {loading ? "Sending..." : "Send Reset Link"}
            </button>
          </form>

          <div className="auth-footer">
            Remember your password?{" "}
            <Link to="/login" className="auth-link">
              Sign In
            </Link>
          </div>
        </div>
      </div>
    </BackgroundWrapper>
  );
};

export default ForgotPassword;
